import React, { useState, useCallback, useEffect } from 'react';
import { Slider, Rail, Handles, Tracks } from 'react-compound-slider';
import i18next from 'i18next';
import {
  Bar,
  RangeSpan,
  RangeSpanItem,
  InputTextWrapper,
  InputTextItem,
  Slider as OwnSlider,
  Track as OwnTrack,
  Handle as OwnHandle,
} from './wrapper';
import { Handle } from './handle';
import { Track } from './track';
import { numberSeparator } from '../../../../utils/formats';
import InputLabel from '../../../../StyledComponents/InputLabel';
import InputText from '../../../../StyledComponents/InputText';

const round = (values: readonly number[], step: number) =>
  values.map((v) => Math.round(v / step) * step);

const getSteps = (steps: number[]) => {
  let current = Number.MAX_SAFE_INTEGER;
  steps.forEach((step, index) => {
    if (index !== 0) {
      const c = step - steps[index - 1];
      if (c < current) {
        current = c;
      }
    }
  });
  return current;
};

interface RangeSliderProps {
  loading?: boolean;
  domain: number[];
  values: number[];
  steps: number[];
  unit?: string;
  formatValues?: boolean;
  maxPrefix?: string;
  onChange: (values: readonly number[]) => void;
}

const RangeSlider = ({
  loading,
  values,
  domain,
  steps,
  unit,
  formatValues,
  maxPrefix,
  onChange,
}: RangeSliderProps) => {
  const [current, setCurrent] = useState<number[]>(values);
  const [inputState, setInputState] = useState({
    values: {
      min: values[0].toString(),
      max: values[1].toString(),
    },
    validation: {
      min: true,
      max: true,
    },
  });

  const onUpdate = useCallback(
    (nextValues: readonly number[]) => {
      if (!Number.isNaN(nextValues[0]) && !Number.isNaN(nextValues[1])) {
        const roundedValues = round(nextValues, step);

        setCurrent(roundedValues.slice());
        setInputState({
          values: {
            min: roundedValues[0].toString(),
            max: roundedValues[1].toString(),
          },
          validation: {
            min: true,
            max: true,
          },
        });
      }
    },
    [values]
  );

  const onChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.currentTarget.name as 'min' | 'max';
    const value = e.currentTarget.value;
    const asNumber = parseInt(value, 10);
    const valid = !isNaN(asNumber) && asNumber >= domain[0] && asNumber <= domain[1];
    setInputState({
      ...inputState,
      values: {
        ...inputState.values,
        [name]: value,
      },
      validation: {
        ...inputState.validation,
        [name]: valid,
      },
    });
  };

  const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const name = e.currentTarget.name as 'min' | 'max';
    if (inputState.validation.min && inputState.validation.max) {
      const v = [parseInt(inputState.values.min, 10), parseInt(inputState.values.max, 10)];
      onChange(v);
    }

    if (!inputState.validation[name]) {
      const nameIndex = name === 'min' ? 0 : 1;
      const next = {
        ...inputState,
        values: {
          ...inputState.values,
          [name]: domain[nameIndex],
        },
        validation: {
          ...inputState.validation,
          [name]: true,
        },
      };
      setInputState(next);
      const v = [parseInt(next.values.min, 10), parseInt(next.values.max, 10)];
      onChange(v);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.code === 'Enter') {
      const name = e.currentTarget.name as 'min' | 'max';
      if (inputState.validation.min && inputState.validation.max) {
        const v = [parseInt(inputState.values.min, 10), parseInt(inputState.values.max, 10)];
        onChange(v);
      }

      if (!inputState.validation[name]) {
        const nameIndex = name === 'min' ? 0 : 1;
        const next = {
          ...inputState,
          values: {
            ...inputState.values,
            [name]: domain[nameIndex],
          },
          validation: {
            ...inputState.validation,
            [name]: true,
          },
        };
        setInputState(next);
        const v = [parseInt(next.values.min, 10), parseInt(next.values.max, 10)];
        onChange(v);
      }
    }
  };

  const onChangeSlider = (values: readonly number[]) => {
    const roundedValues = round(values, step);
    onChange(roundedValues);
  };

  const step = getSteps(steps);

  const currentMin = formatValues ? numberSeparator(current[0]) : current[0];
  const currentMax = formatValues ? numberSeparator(current[1]) : current[1];
  const equal = domain[0] === domain[1];
  const _maxPrefix = maxPrefix && domain[1] === current[1] ? maxPrefix : undefined;

  useEffect(() => {
    if (values[0] !== current[0] || values[1] !== current[1]) {
      setCurrent(values);
    }
  }, [values]);

  const [disableStep, setDisableStep] = useState(true);

  return (
    <>
      {equal ? (
        <OwnSlider>
          <Bar>
            <OwnTrack style={{ left: '0', width: '100%' }} />
            <OwnHandle role="slider" aria-disabled="true" aria-label={i18next.t('search.min')} style={{ left: '0' }} />
            <OwnHandle role="slider" aria-disabled="true" aria-label={i18next.t('search.max')} style={{ left: '100%' }} />
          </Bar>
        </OwnSlider>
      ) : (
        <>
          <InputTextWrapper>
            <InputTextItem>
              <InputLabel>{i18next.t('search.from')}</InputLabel>
              <InputText
                label={i18next.t('search.min')}
                value={inputState.values.min}
                name="min"
                placeholder=""
                onChange={onChangeInput}
                onKeyDown={onKeyDown}
                onBlur={onBlur}
                bordered
                unit={unit}
                inputMode="numeric"
                pattern="[0-9]+"
              />
            </InputTextItem>
            <InputTextItem>
              <InputLabel>{i18next.t('search.to')}</InputLabel>
              <InputText
                label={i18next.t('search.max')}
                value={inputState.values.max}
                name="max"
                placeholder=""
                onChange={onChangeInput}
                onKeyDown={onKeyDown}
                onBlur={onBlur}
                bordered
                unit={unit}
                inputMode="numeric"
                pattern="[0-9]+"
              />
            </InputTextItem>
          </InputTextWrapper>
          <OwnSlider>
            <Slider
              disabled={loading}
              mode={1}
              step={disableStep ? 1 : step}
              domain={domain}
              onSlideStart={() => setDisableStep(false)}
              onSlideEnd={() => setDisableStep(true)}
              onChange={onChangeSlider}
              onUpdate={onUpdate}
              values={values}
              rootStyle={{ position: 'relative' }}
            >
              <Rail>{({ getRailProps }) => <Bar {...getRailProps()} />}</Rail>
              <Handles>
                {({ handles, getHandleProps }) => (
                  <div className="slider-handles">
                    {handles.map((handle, index) => (
                      <Handle
                        key={handle.id}
                        handle={handle}
                        domain={domain}
                        getHandleProps={getHandleProps}
                        aria-label={index === 0 ? i18next.t('search.min') : i18next.t('search.max')}
                      />
                    ))}
                  </div>
                )}
              </Handles>
              <Tracks left={false} right={false}>
                {({ tracks, getTrackProps }) => (
                  <div className="slider-tracks">
                    {tracks.map(({ id, source, target }) => (
                      <Track
                        key={id}
                        source={source}
                        target={target}
                        getTrackProps={getTrackProps}
                      />
                    ))}
                  </div>
                )}
              </Tracks>
            </Slider>
          </OwnSlider>
        </>
      )}
      <RangeSpan>
        <RangeSpanItem>{`${currentMin}${unit ? ` ${unit}` : ''}`}</RangeSpanItem>
        <RangeSpanItem>{`${currentMax}${_maxPrefix ? _maxPrefix : ''}${
          unit ? ` ${unit}` : ''
        }`}</RangeSpanItem>
      </RangeSpan>
    </>
  );
};

export default RangeSlider;
